import { createContext, useCallback, useContext, useMemo, useState, useEffect, ReactNode } from 'react'
import { Restaurant } from '@/features/menu-editor/types'
import { useAuth } from './AuthContext'
import { apiClient } from '@/api/client'
import { setErrorReportingRestaurant } from '@/lib/errorReporting'

const STORAGE_KEY = 'selectedRestaurant'
const FETCH_ATTEMPTS = 3

interface RestaurantContextType {
  selectedRestaurant: Restaurant | null
  setSelectedRestaurant: (restaurant: Restaurant) => void
  clearSelectedRestaurant: () => void
  isRestaurantLocked: boolean // True if user can only access one restaurant
  /** True when loading the user's restaurant failed and there is nothing to show.
   *  Consumers that wait for selectedRestaurant must render a retry UI instead
   *  of an endless spinner when this is set. */
  restaurantLoadError: boolean
  retryRestaurantLoad: () => void
}

const RestaurantContext = createContext<RestaurantContextType | null>(null)

function getStoredRestaurantId(): number | null {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) return null

  try {
    const id = (JSON.parse(stored) as { id?: unknown }).id
    return typeof id === 'number' ? id : null
  } catch {
    return null
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export function RestaurantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [selectedRestaurant, setSelectedRestaurantState] = useState<Restaurant | null>(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        return JSON.parse(stored) as Restaurant
      } catch {
        return null
      }
    }
    return null
  })
  const [isRestaurantLocked, setIsRestaurantLocked] = useState(false)
  const selectedRestaurantId = selectedRestaurant?.id ?? null
  useEffect(() => setErrorReportingRestaurant(selectedRestaurantId), [selectedRestaurantId])
  const [restaurantLoadError, setRestaurantLoadError] = useState(false)

  const fetchAndSetRestaurant = useCallback(async (id: number, options?: { force?: boolean }) => {
    setRestaurantLoadError(false)
    for (let attempt = 1; attempt <= FETCH_ATTEMPTS; attempt++) {
      try {
        const response = await apiClient.get('/api/admin/restaurants')
        const restaurants = response.data as Restaurant[]
        const restaurant = restaurants.find((r) => r.id === id) ?? null
        if (!options?.force && getStoredRestaurantId() !== id) {
          return
        }
        if (restaurant) {
          setSelectedRestaurantState(restaurant)
          localStorage.setItem(STORAGE_KEY, JSON.stringify(restaurant))
        } else if (options?.force) {
          // Definitive answer without the expected restaurant: surface it,
          // a locked restaurant_admin has nothing else to fall back to.
          setRestaurantLoadError(true)
        }
        return
      } catch {
        if (attempt === FETCH_ATTEMPTS) {
          // Out of retries — let the UI offer a manual retry instead of
          // leaving the user on an infinite spinner.
          setRestaurantLoadError(true)
        } else {
          await sleep(1000 * attempt)
        }
      }
    }
  }, [])

  const syncRestaurantForUser = useCallback(() => {
    if (user?.role === 'restaurant_admin' && user.restaurantId) {
      setIsRestaurantLocked(true)
      // Clear wrong restaurant immediately
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        try {
          if ((JSON.parse(stored) as { id?: number }).id !== user.restaurantId) {
            setSelectedRestaurantState(null)
            localStorage.removeItem(STORAGE_KEY)
          }
        } catch {
          localStorage.removeItem(STORAGE_KEY)
        }
      }
      void fetchAndSetRestaurant(user.restaurantId, { force: true })
    } else {
      setIsRestaurantLocked(false)
      // Refresh stored restaurant to keep data (icon, name) fresh
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as { id?: number }
          if (parsed.id) void fetchAndSetRestaurant(parsed.id)
        } catch { /* ignore */ }
      }
    }
  }, [user, fetchAndSetRestaurant])

  useEffect(() => {
    syncRestaurantForUser()
  }, [syncRestaurantForUser])

  const setSelectedRestaurant = useCallback((restaurant: Restaurant) => {
    // Restaurant admins cannot change their restaurant
    if (isRestaurantLocked) return

    setSelectedRestaurantState(restaurant)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(restaurant))
  }, [isRestaurantLocked])

  const clearSelectedRestaurant = useCallback(() => {
    // Restaurant admins cannot clear their restaurant
    if (isRestaurantLocked) return

    setSelectedRestaurantState(null)
    localStorage.removeItem(STORAGE_KEY)
  }, [isRestaurantLocked])

  const value = useMemo(
    () => ({
      selectedRestaurant,
      setSelectedRestaurant,
      clearSelectedRestaurant,
      isRestaurantLocked,
      restaurantLoadError,
      retryRestaurantLoad: syncRestaurantForUser,
    }),
    [selectedRestaurant, setSelectedRestaurant, clearSelectedRestaurant, isRestaurantLocked, restaurantLoadError, syncRestaurantForUser]
  )

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  )
}

export function useRestaurant() {
  const context = useContext(RestaurantContext)
  if (!context) {
    throw new Error('useRestaurant must be used within a RestaurantProvider')
  }
  return context
}
